import { useEffect, useState } from 'react';
import { MessageSquare, Loader2, AlertCircle, Plus, CheckCircle, Clock, XCircle, MessageCircle, Send, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { purchasingAPI, SupplierQuery, SupplierQueryType, SupplierQueryPriority } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import CreateQueryModal from './SupplierQueryModals';

interface SupplierQueryTabProps {
  lineId: number;
  partNumber?: string;
  batchSerialNo?: string;
  onLineUpdate?: () => void;
}

export default function SupplierQueryTab({ lineId, partNumber, batchSerialNo, onLineUpdate }: SupplierQueryTabProps) {
  const { user } = useAuth();
  const [queries, setQueries] = useState<SupplierQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [respondModalOpen, setRespondModalOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<SupplierQuery | null>(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingQueryData, setPendingQueryData] = useState<{
    query_type: SupplierQueryType;
    query_text: string;
    reference_value?: string;
    priority?: SupplierQueryPriority;
    reason_code?: number;
  } | null>(null);

  const isSupplier = user?.platform === 'SUPPLIER';
  const isCustomer = user?.platform === 'CUSTOMER' || user?.platform === 'HELIX';

  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchQueries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getSupplierQueriesByPOLine(lineId);
      setQueries(response.data.data || []);
    } catch (err: unknown) {
      console.error('Failed to fetch queries:', err);
      setError(getApiErrorMessage(err, 'Failed to load queries'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuery = async (data: {
    query_type: SupplierQueryType;
    query_text: string;
    reference_value?: string;
    priority?: SupplierQueryPriority;
    reason_code?: number;
  }) => {
    // CR-3918: Customers see a confirmation dialog before submitting
    if (isCustomer) {
      setPendingQueryData(data);
      setConfirmModalOpen(true);
      return;
    }
    await submitQuery(data);
  };

  const submitQuery = async (data: {
    query_type: SupplierQueryType;
    query_text: string;
    reference_value?: string;
    priority?: SupplierQueryPriority;
    reason_code?: number;
  }) => {
    try {
      await purchasingAPI.createSupplierQuery({
        po_line: lineId,
        ...data,
      });
      toast.success('Query submitted successfully');
      setCreateModalOpen(false);
      fetchQueries();
      onLineUpdate?.();
    } catch (err: unknown) {
      console.error('Failed to create query:', err);
      toast.error(getApiErrorMessage(err, 'Failed to submit query'));
      throw err;
    }
  };

  const handleConfirmSubmit = async () => {
    if (!pendingQueryData) return;
    try {
      await submitQuery(pendingQueryData);
    } finally {
      setConfirmModalOpen(false);
      setPendingQueryData(null);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmModalOpen(false);
    setPendingQueryData(null);
    setCreateModalOpen(false);
  };

  const handleAcknowledge = async (queryId: number) => {
    try {
      setSubmitting(true);
      await purchasingAPI.acknowledgeSupplierQuery(queryId);
      toast.success('Query acknowledged');
      fetchQueries();
    } catch (err: unknown) {
      console.error('Failed to acknowledge query:', err);
      toast.error(getApiErrorMessage(err, 'Failed to acknowledge query'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespond = async (queryId: number, markResolved: boolean = false) => {
    if (!responseText.trim()) {
      toast.error('Please enter a response');
      return;
    }

    try {
      setSubmitting(true);
      await purchasingAPI.respondToSupplierQuery(queryId, {
        response_text: responseText,
        mark_resolved: markResolved,
      });
      toast.success(markResolved ? 'Query resolved' : 'Response submitted');
      setRespondModalOpen(false);
      setResponseText('');
      setSelectedQuery(null);
      fetchQueries();
    } catch (err: unknown) {
      console.error('Failed to respond to query:', err);
      toast.error(getApiErrorMessage(err, 'Failed to submit response'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (queryId: number) => {
    try {
      setSubmitting(true);
      await purchasingAPI.resolveSupplierQuery(queryId, {});
      toast.success('Query resolved');
      fetchQueries();
    } catch (err: unknown) {
      console.error('Failed to resolve query:', err);
      toast.error(getApiErrorMessage(err, 'Failed to resolve query'));
    } finally {
      setSubmitting(false);
    }
  };

  const { formatDateTime: contextFormatDateTime } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDateTime(dateString);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string; icon: LucideIcon }> = {
      open: { label: 'Open', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
      acknowledged: { label: 'Acknowledged', className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: CheckCircle },
      resolved: { label: 'Resolved', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      closed: { label: 'Closed', className: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig['open'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    if (priority === 'normal') return null;
    const priorityConfig: Record<string, { label: string; className: string }> = {
      low: { label: 'Low', className: 'bg-gray-100 text-gray-700' },
      high: { label: 'High', className: 'bg-red-100 text-red-700' },
    };

    const config = priorityConfig[priority];
    if (!config) return null;

    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getQueryTypeLabel = (queryType: string) => {
    const labels: Record<string, string> = {
      part_number: 'Part Number',
      batch_number: 'Batch/Serial Number',
      serial_number: 'Serial Number',
      delivery: 'Delivery',
      quantity: 'Quantity',
      general: 'General',
    };
    return labels[queryType] || queryType;
  };

  const openRespondModal = (query: SupplierQuery) => {
    setSelectedQuery(query);
    setResponseText('');
    setRespondModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchQueries}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">Supplier Queries</h3>
          <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-sm">
            {queries.length}
          </span>
        </div>
        {(isSupplier || isCustomer) && (
          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Raise Query
          </button>
        )}
      </div>

      {/* Queries List */}
      {queries.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-muted/20">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">No queries found for this PO line</p>
          {(isSupplier || isCustomer) && (
            <p className="text-sm text-muted-foreground mt-2">
              Click &quot;Raise Query&quot; to ask about part numbers, batch numbers, or other details
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {queries.map((query) => (
            <div key={query.id} className="border rounded-lg p-4 bg-white shadow-sm">
              {/* Query Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-primary">Query #{query.id}</span>
                      {getStatusBadge(query.status)}
                      {getPriorityBadge(query.priority)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary rounded">
                        {getQueryTypeLabel(query.query_type)}
                      </span>
                      {query.reference_value && (
                        <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                          {query.reference_value}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground text-right">
                  <div>Created by {query.created_by_details?.full_name || 'Unknown'}</div>
                  <div>{formatDate(query.created_at)}</div>
                </div>
              </div>

              {/* Query Text */}
              <div className="bg-muted/30 rounded-lg p-3 mb-3">
                <div className="flex items-start gap-2">
                  <MessageCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{query.query_text}</p>
                </div>
              </div>

              {/* Response Section */}
              {query.response_text && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <Send className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-green-800">Response</span>
                        <span className="text-xs text-green-600">
                          by {query.responded_by_details?.full_name || 'Customer'} on {formatDate(query.responded_at || '')}
                        </span>
                      </div>
                      <p className="text-sm text-green-900">{query.response_text}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Resolution Notes */}
              {query.resolution_notes && query.status === 'resolved' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-800">Resolution</span>
                        <span className="text-xs text-gray-600">
                          by {query.resolved_by_details?.full_name || 'Unknown'} on {formatDate(query.resolved_at || '')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900">{query.resolution_notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {isCustomer && query.status !== 'resolved' && query.status !== 'closed' && (
                <div className="flex items-center gap-2 pt-3 border-t">
                  {query.status === 'open' && (
                    <button
                      onClick={() => handleAcknowledge(query.id)}
                      disabled={submitting}
                      className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm disabled:opacity-50"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Acknowledge
                    </button>
                  )}
                  <button
                    onClick={() => openRespondModal(query)}
                    disabled={submitting}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm disabled:opacity-50"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Respond
                  </button>
                  <button
                    onClick={() => handleResolve(query.id)}
                    disabled={submitting}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-sm disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Resolve
                  </button>
                </div>
              )}

              {isSupplier && query.status !== 'resolved' && query.status !== 'closed' && query.response_text && (
                <div className="flex items-center gap-2 pt-3 border-t">
                  <button
                    onClick={() => handleResolve(query.id)}
                    disabled={submitting}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-sm disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Mark as Resolved
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Query Modal */}
      <CreateQueryModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateQuery}
        partNumber={partNumber}
        batchSerialNo={batchSerialNo}
      />

      {/* CR-3918: Customer confirmation dialog before submitting query */}
      {confirmModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                Confirm Query Submission
              </h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground">
                A notification will be sent to the supplier regarding your query, and the PO line will be placed on hold. Are you sure you want to continue?
              </p>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={handleConfirmCancel}
                className="px-4 py-2 border rounded-lg hover:bg-muted transition-colors"
              >
                No
              </button>
              <button
                onClick={handleConfirmSubmit}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Respond Modal */}
      {respondModalOpen && selectedQuery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Respond to Query #{selectedQuery.id}</h3>
            </div>
            <div className="p-4">
              <div className="mb-4 p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium text-muted-foreground mb-1">Query:</p>
                <p className="text-sm">{selectedQuery.query_text}</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Your Response</label>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg h-32 resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your response..."
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setRespondModalOpen(false);
                  setSelectedQuery(null);
                  setResponseText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRespond(selectedQuery.id, false)}
                disabled={submitting || !responseText.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                Send Response
              </button>
              <button
                onClick={() => handleRespond(selectedQuery.id, true)}
                disabled={submitting || !responseText.trim()}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                Respond & Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
